import React, { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthProvider'
import { Button } from '../ui/button'
import { Menu, X } from 'lucide-react'

export default function MarketingLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { session } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navLinkClass = (path) =>
    `text-sm font-medium transition-colors duration-150 px-1 pb-0.5 ${
      location.pathname === path
        ? 'text-gold border-b-2 border-gold'
        : 'text-charcoal-muted hover:text-charcoal'
    }`

  const mobileNavLinkClass = (path) =>
    `block px-4 py-3 text-sm font-medium border-b border-surface-border transition-colors duration-150 ${
      location.pathname === path
        ? 'text-gold bg-gold/5'
        : 'text-charcoal hover:text-gold hover:bg-gold/5'
    }`

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <div className="min-h-screen bg-background-light">
      {/* Marketing Navigation */}
      <header className="fixed top-0 z-50 w-full border-b border-surface-border bg-white/70 backdrop-blur-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" onClick={closeMobileMenu} className="text-xl font-bold font-display text-charcoal hover:text-gold transition-colors duration-150">
              Claritas
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/how-it-works" className={navLinkClass('/how-it-works')}>
                How It Works
              </Link>
              <Link to="/for-parishes" className={navLinkClass('/for-parishes')}>
                For Parishes
              </Link>
              <Link to="/for-donors" className={navLinkClass('/for-donors')}>
                For Donors
              </Link>
              <Link to="/security" className={navLinkClass('/security')}>
                Security
              </Link>
              <Link to="/pricing" className={navLinkClass('/pricing')}>
                Pricing
              </Link>
            </nav>

            {/* Desktop CTA buttons */}
            <div className="hidden md:flex items-center space-x-3">
              {session ? (
                <Button onClick={() => navigate('/app')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                  Go to App
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/sign-in')}>
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                    Get Started
                  </Button>
                </>
              )}
            </div>

            {/* Mobile: CTA + hamburger */}
            <div className="flex md:hidden items-center gap-2">
              {session ? (
                <Button size="sm" onClick={() => { navigate('/app'); closeMobileMenu(); }} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                  Go to App
                </Button>
              ) : (
                <Button size="sm" onClick={() => { navigate('/sign-in'); closeMobileMenu(); }} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                  Get Started
                </Button>
              )}
              <button
                onClick={() => setMobileMenuOpen((o) => !o)}
                className="p-2 rounded text-charcoal-muted hover:text-charcoal hover:bg-surface-border/40 transition-colors duration-150 focus:outline-none"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-surface-border bg-white/95 backdrop-blur-sm">
            <nav className="flex flex-col">
              <Link to="/how-it-works" className={mobileNavLinkClass('/how-it-works')} onClick={closeMobileMenu}>
                How It Works
              </Link>
              <Link to="/for-parishes" className={mobileNavLinkClass('/for-parishes')} onClick={closeMobileMenu}>
                For Parishes
              </Link>
              <Link to="/for-donors" className={mobileNavLinkClass('/for-donors')} onClick={closeMobileMenu}>
                For Donors
              </Link>
              <Link to="/security" className={mobileNavLinkClass('/security')} onClick={closeMobileMenu}>
                Security
              </Link>
              <Link to="/pricing" className={mobileNavLinkClass('/pricing')} onClick={closeMobileMenu}>
                Pricing
              </Link>
              {!session && (
                <div className="px-4 py-3">
                  <Button variant="ghost" className="w-full justify-center" onClick={() => { navigate('/sign-in'); closeMobileMenu(); }}>
                    Sign In
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      <div className="pt-16">
        <Outlet />
      </div>
    </div>
  )
}
